import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneMissed, PhoneCall, Phone, Check, CheckCheck, Bike, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function formatWhen(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function MissedCallsList() {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/dealer/missed-calls");
            setItems(r.data?.items || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => { load(); }, []);

    const markRead = async (id) => {
        try {
            await api.post(`/dealer/missed-calls/${id}/read`);
            setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
        } catch {
            toast.error("Konnte nicht als gelesen markiert werden");
        }
    };

    const markAllRead = async () => {
        setBusy(true);
        try {
            await api.post("/dealer/missed-calls/read-all");
            setItems((prev) => prev.map((m) => ({ ...m, read: true })));
            toast.success("Alle als gelesen markiert");
        } catch {
            toast.error("Aktion fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (items === null) {
        return <div className="flex items-center gap-2 text-gray-500 text-sm py-8" data-testid="missed-calls-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade verpasste Anrufe…</div>;
    }

    if (items.length === 0) {
        return (
            <div className="text-center py-12 border border-dashed border-gray-200 rounded-sm" data-testid="missed-calls-empty">
                <PhoneMissed className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <div className="font-bold text-[#111827]">Keine verpassten Anrufe</div>
                <div className="text-sm text-gray-500 mt-1">Sobald ein Interessent anruft und nicht erreicht, erscheint er hier.</div>
            </div>
        );
    }

    const unread = items.filter((m) => !m.read).length;

    return (
        <div data-testid="missed-calls-list">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#111827] flex items-center gap-2">
                    <PhoneMissed className="w-5 h-5 text-red-500" /> Verpasste Anrufe & Rückrufe
                    {unread > 0 && <span className="text-[11px] font-mono-num px-1.5 py-0.5 bg-red-500 text-white rounded-sm" data-testid="missed-calls-unread-count">{unread}</span>}
                </h3>
                {unread > 0 && (
                    <button onClick={markAllRead} disabled={busy} className="text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:underline inline-flex items-center gap-1" data-testid="missed-calls-mark-all-read">
                        <CheckCheck className="w-4 h-4" /> Alle gelesen
                    </button>
                )}
            </div>
            <div className="space-y-2">
                {items.map((m) => {
                    const isCallback = m.source === "callback_request";
                    const Icon = isCallback ? PhoneCall : PhoneMissed;
                    return (
                    <div
                        key={m.id}
                        className={`flex items-start gap-3 p-3 border rounded-sm ${m.read ? "border-gray-100 bg-white" : isCallback ? "border-blue-200 bg-blue-50" : "border-red-200 bg-red-50"}`}
                        data-testid={`missed-call-${m.id}`}
                    >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.read ? "bg-gray-100 text-gray-400" : isCallback ? "bg-blue-100 text-blue-600" : "bg-red-100 text-red-600"}`}>
                            <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-[#111827] text-sm truncate flex items-center gap-2">
                                <span className="truncate">{m.caller_name ? `${m.caller_name} · ` : ""}{m.caller_number}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider shrink-0 ${isCallback ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`} data-testid={`missed-call-kind-${m.id}`}>
                                    {isCallback ? "Rückruf erbeten" : "Verpasst"}
                                </span>
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                                <span>{formatWhen(m.called_at || m.created_at)}</span>
                                {m.preferred_time && (
                                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.preferred_time}</span>
                                )}
                                {m.listing_title && (
                                    <Link to={`/listing/${m.listing_id}`} className="inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid={`missed-call-listing-${m.id}`}>
                                        <Bike className="w-3 h-3" /> {m.listing_title}
                                    </Link>
                                )}
                                {m.source === "test" && <span className="text-[10px] px-1 bg-gray-200 text-gray-600 rounded-sm">TEST</span>}
                            </div>
                            {m.note && (
                                <div className="text-xs text-gray-700 mt-1.5 bg-white/70 border border-gray-100 rounded-sm px-2 py-1" data-testid={`missed-call-note-${m.id}`}>
                                    „{m.note}"
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a href={`tel:${(m.caller_number || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#111827] hover:bg-black text-[#FFCC00] text-[11px] font-bold uppercase tracking-wider rounded-sm" data-testid={`missed-call-callback-${m.id}`}>
                                <Phone className="w-3.5 h-3.5" /> Zurückrufen
                            </a>
                            {!m.read && (
                                <button onClick={() => markRead(m.id)} className="text-gray-400 hover:text-emerald-600" title="Als gelesen markieren" data-testid={`missed-call-read-${m.id}`}>
                                    <Check className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}
